import { Button, FormField } from '@components';
import { ApiEndpoint, columnDirection, Dictionaries, FormType, InputMod, InputType, Translation } from '@enums';
import i18n from '@translations';
import type { DualListBoxProps, EndpointType, FormConfig, FormConfigMapping, FormConfigMappingReturn } from '@types';
import { getFilteredOptionList, getOptionListFromCatalog } from '@utils';

export const getDualListBoxConfig = (): DualListBoxProps => {
  return {
    name: 'country_black_list',
    options: getOptionListFromCatalog({ name: Dictionaries.Regions }),
    leftPanel: {
      title: i18n.t(`${Translation.Configuration}:modals.security.availableRegions`),
      fieldName: 'availableRegions',
      placeholder: i18n.t(`${Translation.Configuration}:forms.security.searchByRegionName`),
    },
    rightPanel: {
      title: i18n.t(`${Translation.Configuration}:modals.security.blockedRegions`),
      fieldName: 'blockedRegions',
      placeholder: i18n.t(`${Translation.Configuration}:forms.security.searchByRegionName`),
    },
    direction: columnDirection.Row,
    showValue: true,
  };
};

interface GetSecurityFormConfigParams {
  endpoint?: EndpointType;
  loading?: boolean;
  openUserAgentModal?: () => void;
  openUserIpModal?: () => void;
  openBlockRegionModal?: () => void;
}

export const getSecurityFormConfig = ({
  endpoint = ApiEndpoint.DEFAULT,
  loading,
  openUserAgentModal,
  openUserIpModal,
  openBlockRegionModal,
}: GetSecurityFormConfigParams): FormConfig => {
  const SECURITY_FORM: FormConfigMapping = {
    [ApiEndpoint.DEFAULT]: {
      formType: FormType.CdnSecurity,
      endpoint: ApiEndpoint.DEFAULT,
      inputList: [
        {
          name: 'under_attack_mode',
          defaultValue: false,
          label: i18n.t(`${Translation.Description}:configuration.security.underAttackMode`),
          description: i18n.t(`${Translation.Description}:configuration.security.underAttackMode_description`),
          placeholder: i18n.t(`${Translation.Description}:configuration.security.selectYourUnderAttackMode`),
          inputType: InputType.Switch,
          fullWidth: true,
          centerItem: true,
          withoutLabel: true,
          options: getOptionListFromCatalog({ name: Dictionaries.UnderAttackMode }),
          disabled: loading,
        },
        {
          name: 'soft_request_limit',
          type: 'number',
          min: 1,
          inputMod: InputMod.Filled,
          title: i18n.t(`${Translation.Description}:configuration.security.limitRequestRatePerIP`),
          label: i18n.t(`${Translation.Configuration}:forms.security.limitRequestRate`),
          description: i18n.t(`${Translation.Description}:configuration.security.limitRequestRatePerIP_description`),
          placeholder: i18n.t(`${Translation.Configuration}:forms.security.limitRequestRateRange`),
          textInputClassName: 'bg-white',
          withoutLabel: true,
          loading,
          adornment: (
            <FormField
              label={i18n.t(`${Translation.Configuration}:forms.security.unit`)}
              name="soft_request_limit_unit"
              inputType={InputType.Select}
              withoutLabel
              inputMod={InputMod.Filled}
              centerItem
              textInputClassName="bg-transparent ring-0 focus:ring-0"
              labelClassName="top-0.5"
              customWidth="sm:w-16 w-20"
              className="flex border-l"
              loading={loading}
              placeholder={i18n.t(`${Translation.Configuration}:forms.security.selectYourUnit`)}
              options={getFilteredOptionList({
                dictionaries: Dictionaries.RequestRateUnit,
                availableVariables: ['qps'],
              })}
            />
          ),
        },
        {
          name: 'block_request_limit',
          type: 'number',
          min: 1,
          inputMod: InputMod.Filled,
          title: i18n.t(`${Translation.Description}:configuration.security.rejectRequestPerIP`),
          label: i18n.t(`${Translation.Configuration}:forms.security.rejectRequest`),
          description: i18n.t(`${Translation.Description}:configuration.security.rejectRequestPerIP_description`),
          placeholder: i18n.t(`${Translation.Configuration}:forms.security.limitRequestRateRange`),
          textInputClassName: 'bg-white',
          withoutLabel: true,
          loading,
          adornment: (
            <FormField
              label={i18n.t(`${Translation.Configuration}:forms.security.unit`)}
              name="block_request_limit_unit"
              inputType={InputType.Select}
              withoutLabel
              inputMod={InputMod.Filled}
              centerItem
              customWidth="sm:w-16 w-20"
              textInputClassName="bg-transparent ring-0 focus:ring-0"
              labelClassName="top-0.5"
              className="flex border-l"
              loading={loading}
              placeholder={i18n.t(`${Translation.Configuration}:forms.security.selectYourUnit`)}
              options={getFilteredOptionList({
                dictionaries: Dictionaries.RequestRateUnit,
                availableVariables: ['qps'],
              })}
            />
          ),
        },
        {
          name: 'block_action',
          label: i18n.t(`${Translation.Description}:configuration.security.rejectAction`),
          description: i18n.t(`${Translation.Description}:configuration.security.rejectAction_description`),
          placeholder: i18n.t(`${Translation.Description}:configuration.security.selectYourRejectAction`),
          inputType: InputType.Select,
          inputMod: InputMod.Filled,
          withoutLabel: true,
          options: getOptionListFromCatalog({ name: Dictionaries.DashboardBlockAction }),
          disabled: loading,
        },
        {
          name: 'user_agent_black_list',
          label: i18n.t(`${Translation.Description}:configuration.security.blockUserAgent`),
          description: i18n.t(`${Translation.Description}:configuration.security.blockUserAgent_description`),
          inputType: InputType.Custom,
          disabled: loading,
          withoutLabel: true,
          children: (
            <Button onClick={openUserAgentModal} loading={loading} className="rounded-lg w-full">
              {i18n.t(`${Translation.Configuration}:buttons.security.addUserAgentToBlockList`)}
            </Button>
          ),
        },
        {
          name: 'ip_black_list',
          defaultValue: [],
          label: i18n.t(`${Translation.Description}:configuration.security.blockUserIP`),
          description: i18n.t(`${Translation.Description}:configuration.security.blockUserIP_description`),
          inputType: InputType.Custom,
          disabled: loading,
          withoutLabel: true,
          children: (
            <Button onClick={openUserIpModal} loading={loading} className="rounded-lg w-full">
              {i18n.t(`${Translation.Configuration}:buttons.security.addIPToBlockList`)}
            </Button>
          ),
        },
        {
          name: 'country_black_list',
          title: i18n.t(`${Translation.Description}:configuration.security.blockRegion`),
          description: i18n.t(`${Translation.Description}:configuration.security.blockRegion_description`),
          inputType: InputType.Custom,
          disabled: loading,
          withoutLabel: true,
          children: (
            <Button onClick={openBlockRegionModal} loading={loading} className="rounded-lg w-full">
              {i18n.t(`${Translation.Configuration}:buttons.security.addRegionToBlockList`)}
            </Button>
          ),
        },
      ],
    },
  };

  return SECURITY_FORM?.[endpoint as keyof FormConfigMappingReturn];
};
